<template>
  <div class="log-pro-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$y = logₐ(x)$</div>
    </div>

    <div class="split-layout">
      <!-- 曲线追踪区域 -->
      <div class="simulation-stage dark-stage flex-1">
        <svg width="100%" height="340" viewBox="-100 -300 400 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="log-glow-p" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
           </defs>

           <g stroke="rgba(241, 245, 249, 0.05)" stroke-width="1.5">
              <line v-for="i in 9" :key="'v'+i" :x1="-100 + (i-1)*50" y1="-300" :x2="-100 + (i-1)*50" y2="300" />
              <line v-for="i in 13" :key="'h'+i" x1="-100" :y1="-300 + (i-1)*50" x2="300" :y2="-300 + (i-1)*50" />
           </g>
           
           <line x1="-100" y1="0" x2="300" y2="0" stroke="#475569" stroke-width="2.5" />
           <line x1="0" y1="-300" x2="0" y2="300" stroke="#475569" stroke-width="2.5" />
           <text x="280" y="20" fill="#94a3b8" font-size="16" font-weight="900">X轴</text>
           <text x="10" y="-280" fill="#94a3b8" font-size="16" font-weight="900">Y轴</text>

           <path :d="curvePath" fill="none" stroke="#10b981" stroke-width="5" filter="url(#log-glow-p)" stroke-linecap="round" />
           
           <!-- 常定点 (1,0) -->
           <circle cx="30" cy="0" r="7" fill="#fbbf24" stroke="#fff" stroke-width="2"/>
           <text x="35" y="-15" fill="#fbbf24" font-size="14" font-weight="900">(1, 0)</text>

           <g :transform="`translate(${currentX * 30}, ${-currentY * 30})`">
              <circle r="8" fill="#14b8a6" stroke="#fff" stroke-width="2.5" />
              <line x1="0" y1="0" :x2="0" :y2="currentY * 30" stroke="#94a3b8" stroke-dasharray="6,4" opacity="0.6" />
              <line x1="0" y1="0" :x2="-currentX * 30" y2="0" stroke="#94a3b8" stroke-dasharray="6,4" opacity="0.6" />
           </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item green">
              <label>降维量级 y</label>
              <span class="val">{{ currentY.toFixed(2) }}</span>
           </div>
        </div>
      </div>

      <!-- 物理强度应用模拟 -->
      <div class="simulation-stage logic-panel">
        <div class="logic-header">现实能级映射表 (如: 地震能量级别)</div>
        <div class="physical-bars">
           <div class="bar-row">
              <div class="label">震源原能量 E (模拟X)</div>
              <div class="bar-bg">
                <div class="bar-fill red-fill" :style="`width: ${(currentX / 100) * 100}%;`"></div>
                <div class="bar-text">E = {{ currentX.toFixed(1) }}</div>
              </div>
           </div>
           
           <div class="bar-row mt-l">
              <div class="label">表象破坏力震级 (模拟Y)</div>
              <div class="bar-bg">
                <div class="bar-fill green-fill" :style="`width: ${Math.max(0, currentY) / 3 * 100}%;`"></div>
                <div class="bar-text">M = {{ Math.max(0, currentY).toFixed(2) }}</div>
              </div>
           </div>
           
           <div class="desc-text">
             <i class="el-icon-odometer"></i> 上方能量呈恐怖暴涨趋势（X放大到 100），但经过对数压缩机制后，下方展示的评级（Y）仅从微弱的1递增到了不到3级！<strong>这就是对数的真实魅力：把星系级别的差距压缩到图纸的刻度条上。</strong>
           </div>
        </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
        <div class="control-grid-mini">
           <div class="s-item">
              <label>对数底数 a ({{ base > 1 ? '常态压缩' : '反向映射' }})</label>
              <el-slider v-model="base" :min="0.5" :max="4.0" :step="0.5" size="small"></el-slider>
           </div>
           <div class="s-item">
              <label>实际数量 x / 原能量级 ({{ currentX.toFixed(0) }})</label>
              <!-- 为了显示对数的压缩效果，自变量可以拉得非常大 -->
              <el-slider v-model="currentX" :min="1" :max="100" :step="1" size="small"></el-slider>
           </div>
        </div>
      </div>

      <div class="discovery-card-compact" v-latex>
         <i class="el-icon-data-line" style="color:#0d9488;"></i>
         <div class="discovery-content">
            <p><strong>降维运算：</strong>对数图像以Y轴为渐近线，它是指数的完美逆向存在。在遇到如分贝（噪音等级）、pH值（酸碱度）或里氏震级这种极度悬殊物理量时，对数通过其超缓的增长速率，强制将宏观宇宙与微观本质锁定在同一把尺子上。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LogarithmicFunctionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      base: 2.0,
      currentX: 8.0
    }
  },
  computed: {
    currentY() {
      if (this.base === 1 || this.currentX <= 0) return 0;
      return Math.log(this.currentX) / Math.log(this.base);
    },
    curvePath() {
      let pathArr = [];
      // x limits in svg logic, 1 unit = 30px
      for (let x = 0.05; x <= 12; x += 0.05) {
        if (this.base === 1) continue;
        let y = Math.log(x) / Math.log(this.base);
        if (y > 10 || y < -10) continue;
        pathArr.push(`${(x * 30).toFixed(1)},${(-y * 30).toFixed(1)}`);
      }
      return `M ${pathArr.join(' L ')}`;
    }
  }
}
</script>

<style scoped lang="scss">
.log-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 14px; color: #1e293b; font-weight: 700; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; padding: 6px 16px; border-radius: 20px; font-weight: 900; font-family: monospace; font-size: 15px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 340px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 1; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; }
.logic-header { background: #1e293b; padding: 10px; color: #2dd4bf; font-size: 13px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; }

.physical-bars { padding: 30px 20px; flex: 1; display: flex; flex-direction: column; justify-content: center; }
.bar-row { margin-bottom: 15px; .label { color: #cbd5e1; font-size: 12px; font-weight: 900; margin-bottom: 8px; } }
.mt-l { margin-top: 30px; }
.bar-bg { background: #1e293b; height: 28px; border-radius: 6px; position: relative; overflow: hidden; border: 1px solid #334155; }
.bar-fill { height: 100%; transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
.red-fill { background: linear-gradient(90deg, #f43f5e, #be123c); }
.green-fill { background: linear-gradient(90deg, #10b981, #047857); }
.bar-text { position: absolute; left: 10px; top: 0; line-height: 28px; color: #fff; font-size: 13px; font-weight: 900; font-family: monospace; text-shadow: 0 1px 3px rgba(0,0,0,0.8); }
.desc-text { color: #94a3b8; font-size: 12px; line-height: 1.8; margin-top: 40px; padding: 12px; background: rgba(20, 184, 166, 0.1); border-radius: 8px; border: 1px solid rgba(20, 184, 166, 0.2); strong { color: #5eead4; } }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; right: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 14px; label { display: block; font-size: 11px; color: #94a3b8; font-weight: 900; } .val { font-size: 15px; color: #f8fafc; font-weight: 900; font-family: monospace; } &.green { border-left: 4px solid #10b981; } } } }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 14px 18px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 12px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.discovery-card-compact { background: #f0fdfa; padding: 14px 18px; border-radius: 12px; border: 1px solid #ccfbf1; display: flex; align-items: flex-start; gap: 12px; i { font-size: 1.2rem; margin-top: 2px; } .discovery-content { flex: 1; p { margin: 0; font-size: 13px; color: #0f766e; line-height: 1.6; strong { color: #115e59; font-weight: 900; } } } }
</style>
